import { useEffect, useState } from "react";
import axios from "axios";
import { API, currency, fmtDate } from "@/lib/api";
import { WifiHigh, SignOut, CreditCard } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const portal = axios.create({ baseURL: API });
portal.interceptors.request.use((c) => { const t = localStorage.getItem("portal_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

export default function Portal() {
  const [token, setToken] = useState(localStorage.getItem("portal_token"));
  const [me, setMe] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState([]);
  const [creds, setCreds] = useState({ username: "", password: "" });

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([portal.get("/portal/me"), portal.get("/portal/invoices"), portal.get("/portal/usage?hours=24")]);
      setMe(a.data); setInvoices(b.data); setUsage(c.data.series || []);
    } catch { logout(); }
  };
  useEffect(() => { if (token) load(); }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/portal/login`, creds);
      localStorage.setItem("portal_token", data.token);
      setToken(data.token);
      toast.success(`Welcome, ${data.full_name}`);
    } catch (err) { toast.error("Wrong username or password"); }
  };

  const logout = () => { localStorage.removeItem("portal_token"); setToken(null); setMe(null); };

  const pay = async (id) => {
    try {
      const { data } = await portal.post(`/portal/pay/${id}`);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Checkout URL missing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed — try again"); }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center p-4 grain">
        <form onSubmit={login} className="border border-border bg-card p-8 w-full max-w-sm space-y-4" data-testid="portal-login">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-primary/60 grid place-items-center bg-primary/10"><WifiHigh size={18} weight="bold" className="text-primary" /></div>
            <div><div className="font-display font-bold text-sm leading-none">SNS RADIUS</div><div className="uppercase-label mt-1">Subscriber Portal</div></div>
          </div>
          <div><Label className="uppercase-label">Username</Label><Input required value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} className="mt-1.5 bg-secondary font-mono" data-testid="portal-user" /></div>
          <div><Label className="uppercase-label">Password</Label><Input required type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className="mt-1.5 bg-secondary font-mono" data-testid="portal-pass" /></div>
          <Button type="submit" data-testid="portal-submit" className="w-full rounded-sm bg-primary hover:bg-primary/90">Sign in</Button>
          <div className="text-xs text-muted-foreground">Use the username & password shared by your ISP.</div>
        </form>
      </div>
    );
  }

  if (!me) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Loading…</div>;
  const totalDL = usage.reduce((s, x) => s + (x.in_bytes || 0), 0) / 1e6;
  const totalUL = usage.reduce((s, x) => s + (x.out_bytes || 0), 0) / 1e6;

  return (
    <div className="min-h-screen grain p-4 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="portal-dashboard">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-primary/60 grid place-items-center bg-primary/10"><WifiHigh size={18} weight="bold" className="text-primary" /></div>
          <div><div className="font-display font-bold text-base leading-none">SNS RADIUS</div><div className="uppercase-label mt-1">Subscriber Portal</div></div>
        </div>
        <Button variant="outline" onClick={logout} className="rounded-sm border-border"><SignOut size={14} className="mr-2" /> Sign out</Button>
      </header>

      <div className="border border-border bg-card p-6">
        <div className="uppercase-label">Account</div>
        <h1 className="font-display font-bold text-3xl mt-1">Hi, {me.full_name}</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
          <div><div className="uppercase-label">Plan</div><div className="mt-1 font-medium">{me.plan?.name || "—"}</div><div className="font-mono text-xs text-muted-foreground">{me.plan?.speed_mbps} Mbps</div></div>
          <div><div className="uppercase-label">Status</div><div className="mt-1 inline-flex items-center gap-2"><span className={`pulse-dot ${me.connection_status}`} /> {me.connection_status}</div></div>
          <div><div className="uppercase-label">IP</div><div className="mt-1 font-mono">{me.ip_address || <span className="text-muted-foreground text-xs">auto (BRAS)</span>}</div></div>
          <div><div className="uppercase-label">Username</div><div className="mt-1 font-mono">{me.username}</div></div>
          <div data-testid="portal-pool"><div className="uppercase-label">IP Pool</div><div className="mt-1 font-mono text-primary">{me.ip_pool?.name || me.plan?.address_pool || "—"}</div>{me.ip_pool && <div className="text-[10px] font-mono text-muted-foreground truncate">{me.ip_pool.range_start} → {me.ip_pool.range_end}</div>}</div>
          {me.ip_pool_v6 && <div><div className="uppercase-label">IPv6 Pool</div><div className="mt-1 font-mono text-cyan-400">{me.ip_pool_v6.name}</div></div>}
        </div>
      </div>

      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="uppercase-label">Data usage · last 24h</div>
            <h2 className="font-display text-xl font-bold mt-1">↓ {totalDL.toFixed(1)} MB &nbsp;·&nbsp; ↑ {totalUL.toFixed(1)} MB</h2>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer><AreaChart data={usage.map(u => ({ ...u, in_mb: (u.in_bytes||0)/1e6, out_mb: (u.out_bytes||0)/1e6 }))}>
            <defs><linearGradient id="pdown" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5}/><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid stroke="hsl(var(--border))" vertical={false}/>
            <XAxis dataKey="ts" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => (v || "").slice(11, 16)}/>
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10}/>
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}/>
            <Area type="monotone" dataKey="in_mb" name="DL MB" stroke="hsl(var(--primary))" fill="url(#pdown)" />
            <Area type="monotone" dataKey="out_mb" name="UL MB" stroke="hsl(var(--success))" fillOpacity={0.15}/>
          </AreaChart></ResponsiveContainer>
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="p-6 border-b border-border"><div className="uppercase-label">Billing</div><h2 className="font-display text-xl font-bold mt-1">Invoices</h2></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-border">
            <th className="uppercase-label text-left py-2 px-3">Invoice</th>
            <th className="uppercase-label text-right py-2 px-3">Amount</th>
            <th className="uppercase-label text-left py-2 px-3">Due</th>
            <th className="uppercase-label text-left py-2 px-3">Status</th>
          </tr></thead>
          <tbody>{invoices.map((i) => (
            <tr key={i.id} className="border-b border-border/60">
              <td className="py-2 px-3 font-mono">{i.invoice_no}</td>
              <td className="py-2 px-3 font-mono text-right">{currency(i.amount)}</td>
              <td className="py-2 px-3 font-mono text-muted-foreground">{i.due_date}</td>
              <td className="py-2 px-3"><span className={`text-xs uppercase font-mono px-2 py-0.5 border rounded-sm ${i.status === "paid" ? "text-[hsl(var(--success))] border-[hsl(var(--success)/0.4)]" : i.status === "overdue" ? "text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.4)]" : "text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]"}`}>{i.status}</span></td>
              <td className="py-2 px-3 text-right">
                {i.status !== "paid" && (
                  <Button size="sm" data-testid={`portal-pay-${i.id}`} onClick={() => pay(i.id)} className="rounded-sm bg-primary hover:bg-primary/90 h-7 text-xs">
                    <CreditCard size={12} className="mr-1" /> Pay now
                  </Button>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
    </div>
  );
}
